/**
 * Recover when an open tab asks for a piece of an older build.
 *
 * Each deploy renames the app's lazily loaded chunks. A tab opened before the
 * deploy still asks for the old names; the host no longer has them and answers
 * with index.html, so the browser reports "'text/html' is not a valid
 * JavaScript MIME type" (or "Failed to fetch dynamically imported module").
 * Re-rendering cannot fix that; only loading the new build can. So the first
 * time it happens we reload the page once. A second failure within a minute is
 * shown as an error instead, so a real fault never loops.
 */

const RELOADED_AT_KEY = 'stale-chunk-reload-at';
const LOOP_GUARD_MS = 60_000;

const STALE_CHUNK_PATTERNS = [
  /is not a valid JavaScript MIME type/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
];

export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error ? `${error.name}: ${error.message}` : typeof error === 'string' ? error : '';
  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message));
}

/** Reload once; returns false when we already reloaded within the last minute. */
export function reloadOnceForStaleChunk(
  now: number = Date.now(),
  reload: () => void = () => window.location.reload(),
): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
  } catch {
    // Storage blocked: fall through and reload once anyway.
  }
  if (last && now - last < LOOP_GUARD_MS) return false;
  try {
    sessionStorage.setItem(RELOADED_AT_KEY, String(now));
  } catch {
    // Without storage we cannot guard a loop, but one reload is still right.
  }
  reload();
  return true;
}

/** Vite fires this when preloading a chunk's dependencies fails after a deploy. */
export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  });
}
